/**
 * Property Executive Summary tab – same content as company Executive Summary (About PMC).
 * Mirrors Angular property-edit executivesummary template: same questions, Q7 = main contact radio list, same submit payload.
 */
import { useState, useEffect, useCallback } from 'react'
import { ClipboardCheck, Clock, Loader2, Users } from 'lucide-react'
import { useUserRole } from '@/hooks/useUserRole'
import apiClient from '@/lib/api/apiClient'
import { SectionCard } from '@/components/ui/SectionCard'
import tabStyles from '@/features/company/styles/companyInfoTab.module.css'
import { PROPERTY_ROLE_NAMES } from '../constants'
import type {
  PropertyExecutiveSummaryStaffMember,
  PropertyExecutiveSummaryTabProps,
} from '../types'
import { PropertyExecutiveSummaryButtonGroup } from './PropertyExecutiveSummaryButtonGroup'

export function PropertyExecutiveSummaryTab({
  property,
  propertyId,
  onDataChange,
}: PropertyExecutiveSummaryTabProps) {
  const { hasPolicy } = useUserRole()
  const canEdit = hasPolicy(['property_executive_summary_edit'])
  const questionNonClickable = !canEdit

  const [staffList, setStaffList] = useState<PropertyExecutiveSummaryStaffMember[]>([])
  const [staffLoading, setStaffLoading] = useState(false)

  const [formData, setFormData] = useState({
    income_requirement_criteria: String(property?.income_requirement_criteria ?? ''),
    allow_late_rent: String(property?.allow_late_rent ?? ''),
    eviction_credit_report: String(property?.eviction_credit_report ?? ''),
    time_frame_period: String(property?.time_frame_period ?? ''),
    allow_applicant_move: String(property?.allow_applicant_move ?? ''),
    applicant_purchase_policy: String(property?.applicant_purchase_policy ?? ''),
    main_contact_person: String(property?.main_contact_person ?? ''),
    special_note: String(property?.special_note ?? ''),
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync local form to the property prop when it changes
    setFormData({
      income_requirement_criteria: String(property?.income_requirement_criteria ?? ''),
      allow_late_rent: String(property?.allow_late_rent ?? ''),
      eviction_credit_report: String(property?.eviction_credit_report ?? ''),
      time_frame_period: String(property?.time_frame_period ?? ''),
      allow_applicant_move: String(property?.allow_applicant_move ?? ''),
      applicant_purchase_policy: String(property?.applicant_purchase_policy ?? ''),
      main_contact_person: String(property?.main_contact_person ?? ''),
      special_note: String(property?.special_note ?? ''),
    })
  }, [property])

  useEffect(() => {
    if (!propertyId) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: loading state for the property-staff fetch (data-load effect)
    setStaffLoading(true)
    apiClient
      .get('/api/property_staff', { params: { property_id: propertyId } })
      .then((res) => setStaffList(Array.isArray(res.data) ? res.data : []))
      .catch(() => setStaffList([]))
      .finally(() => setStaffLoading(false))
  }, [propertyId])

  const notifyParent = useCallback(
    (next: Record<string, string>) => {
      const out = { ...next }
      if (out.eviction_credit_report === '1') out.time_frame_period = ''
      onDataChange?.(out)
    },
    [onDataChange],
  )

  useEffect(() => {
    notifyParent(formData)
  }, [formData, notifyParent])

  const handleChange = (field: string, value: string) => {
    if (field === 'eviction_credit_report' && value === '1') {
      setFormData((prev) => ({ ...prev, eviction_credit_report: value, time_frame_period: '' }))
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }))
    }
  }

  const questionClass = 'max-w-4xl text-sm font-medium leading-relaxed text-slate-800'
  const questionBlockClass = 'space-y-3'

  return (
    <div
      data-testid="property-executive-summary-tab"
      className={`${tabStyles.tabContent} animate-in fade-in duration-300`}
    >
      <SectionCard
        icon={ClipboardCheck}
        title="Applicant Eligibility"
        testId="property-executive-summary-section-applicant-eligibility"
        titleTestId="property-executive-summary-section-applicant-eligibility-title"
      >
        <div className="space-y-6">
          <section className={questionBlockClass} data-testid="property-executive-summary-question-1">
            <p className={questionClass} data-testid="property-executive-summary-question-1-prompt">
              1. Does the PMC/Community send applicants to Liberty Rent that do not meet their income requirement?
            </p>
            <PropertyExecutiveSummaryButtonGroup
              name="income_requirement_criteria"
              value={formData.income_requirement_criteria}
              options={[
                { label: 'Yes', value: '1' },
                { label: 'No', value: '0' },
              ]}
              disabled={questionNonClickable}
              onChange={handleChange}
            />
          </section>

          <section className={questionBlockClass} data-testid="property-executive-summary-question-2">
            <p className={questionClass} data-testid="property-executive-summary-question-2-prompt">
              2. Does the PMC/Community allow for a current LR or non LR resident, that has been late on their rent
              multiple times, to reapply/apply with Liberty Rent at renewal?
            </p>
            <PropertyExecutiveSummaryButtonGroup
              name="allow_late_rent"
              value={formData.allow_late_rent}
              options={[
                { label: 'Yes', value: '1' },
                { label: 'No', value: '0' },
              ]}
              disabled={questionNonClickable}
              onChange={handleChange}
            />
          </section>

          <section className={questionBlockClass} data-testid="property-executive-summary-question-3">
            <p className={questionClass} data-testid="property-executive-summary-question-3-prompt">
              3. Does the PMC/Community allow applicants with an eviction on their credit report to move in the
              apartment home?
            </p>
            <PropertyExecutiveSummaryButtonGroup
              name="eviction_credit_report"
              value={formData.eviction_credit_report}
              options={[
                { label: 'Yes', value: '1' },
                { label: 'No', value: '0' },
              ]}
              disabled={questionNonClickable}
              onChange={handleChange}
            />
          </section>

          <section
            className={questionBlockClass}
            data-testid="property-executive-summary-question-4"
            inert={formData.eviction_credit_report !== '0' ? true : undefined}
          >
            <p className={questionClass} data-testid="property-executive-summary-question-4-prompt">
              4. If the answer is &apos;No&apos; to question 3, what is the time frame the PMC/Community will consider?
            </p>
            <PropertyExecutiveSummaryButtonGroup
              name="time_frame_period"
              value={formData.time_frame_period}
              options={[
                { label: 'After 2 yrs', value: 'After 2 yrs' },
                { label: 'After 3 yrs', value: 'After 3 yrs' },
              ]}
              disabled={questionNonClickable}
              onChange={handleChange}
            />
          </section>

          <section className={questionBlockClass} data-testid="property-executive-summary-question-5">
            <p className={questionClass} data-testid="property-executive-summary-question-5-prompt">
              5. Does the PMC/Community allow an applicant to move in with a rent debt?
            </p>
            <PropertyExecutiveSummaryButtonGroup
              name="allow_applicant_move"
              value={formData.allow_applicant_move}
              options={[
                { label: 'Yes', value: '1' },
                { label: 'No', value: '0' },
              ]}
              disabled={questionNonClickable}
              onChange={handleChange}
            />
          </section>
        </div>
      </SectionCard>

      <SectionCard
        icon={Clock}
        title="Unit Hold Policy"
        testId="property-executive-summary-section-unit-hold-policy"
        titleTestId="property-executive-summary-section-unit-hold-policy-title"
      >
        <section className={questionBlockClass} data-testid="property-executive-summary-question-6">
          <p className={questionClass} data-testid="property-executive-summary-question-6-prompt">
            6. What is the PMC/Community internal policy once an applicant is approved by Liberty Rent to purchase their
            policy to hold the unit?
          </p>
          <PropertyExecutiveSummaryButtonGroup
            name="applicant_purchase_policy"
            value={formData.applicant_purchase_policy}
            options={[
              { label: '48 hrs', value: '48 hrs' },
              { label: '72 hrs', value: '72 hrs' },
              { label: 'One Week', value: 'One Week' },
            ]}
            disabled={questionNonClickable}
            onChange={handleChange}
          />
        </section>
      </SectionCard>

      <SectionCard
        icon={Users}
        title="Program Management"
        testId="property-executive-summary-section-program-management"
        titleTestId="property-executive-summary-section-program-management-title"
      >
        <div className="space-y-6">
          <section className={questionBlockClass} data-testid="property-executive-summary-question-7">
            <p className={questionClass} data-testid="property-executive-summary-question-7-prompt">
              7. Who is the main point of contact(s) [person(s) who helps manage the program]?
            </p>
            {staffLoading ? (
              <div
                className="flex items-center gap-2 text-sm text-slate-500"
                data-testid="property-executive-summary-contacts-loading"
              >
                <Loader2 className="h-4 w-4 animate-spin text-[rgb(21_151_255)]" aria-hidden />
                Loading contacts…
              </div>
            ) : staffList.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="property-executive-summary-contacts-empty">
                No contacts yet. Add users from the Contacts tab.
              </p>
            ) : (
              <div
                className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/70 p-3"
                data-testid="property-executive-summary-main-contact-list"
              >
                <ul className="m-0 list-none space-y-2 p-0">
                  {staffList.map((staff, idx) => (
                    <li key={staff.role_id ?? idx}>
                      <label
                        className={`flex cursor-pointer items-start gap-3 ${
                          questionNonClickable ? 'cursor-default opacity-60' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="main_contact_person"
                          value={String(staff.role_id)}
                          checked={formData.main_contact_person === String(staff.role_id)}
                          onChange={() =>
                            !questionNonClickable &&
                            handleChange('main_contact_person', String(staff.role_id))
                          }
                          disabled={questionNonClickable}
                          data-testid={`property-executive-summary-main-contact-${staff.role_id}-radio`}
                          className="mt-1 h-4 w-4 shrink-0 accent-[rgb(21_151_255)]"
                        />
                        <span className="text-sm leading-snug text-slate-800">
                          {staff.name ?? '—'} (
                          {PROPERTY_ROLE_NAMES[staff.role_name ?? ''] ?? staff.role_name ?? '—'})
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <section className={`${questionBlockClass} pb-1`} data-testid="property-executive-summary-question-8">
            <p className={questionClass} data-testid="property-executive-summary-question-8-prompt">
              8. What other parameters does the PMC/Community have that Liberty Rent need to be aware of?
            </p>
            <textarea
              rows={5}
              value={formData.special_note}
              onChange={(e) => handleChange('special_note', e.target.value)}
              disabled={questionNonClickable}
              data-testid="property-executive-summary-special-note-input"
              className="w-full max-w-2xl resize-y rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-60"
            />
          </section>
        </div>
      </SectionCard>
    </div>
  )
}
